// components/cards/PharmacyCard.jsx
import React from 'react';
import { 
  MapPin, Phone, Star, Truck, Shield, 
  Navigation, CheckCircle 
} from 'lucide-react';

const PharmacyCard = ({ 
  pharmacy, 
  viewMode = 'grid', 
  showDistance = false, 
  onPharmacyClick,
  onCallPharmacy 
}) => {
  const isOpen = () => {
    if (!pharmacy.operatingHours) return null;
    
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const hours = pharmacy.operatingHours[currentDay];
    
    if (!hours || !hours.open || !hours.close) return null;
    
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [openHour, openMin] = hours.open.split(':').map(Number);
    const [closeHour, closeMin] = hours.close.split(':').map(Number);
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;
    
    return currentTime >= openTime && currentTime <= closeTime;
  };

  const openStatus = isOpen();
  const primaryImage = pharmacy.images?.[0]?.url || '/api/placeholder/400/250';

  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-300 overflow-hidden">
        <div className="flex flex-col sm:flex-row">
          {/* Image */}
          <div className="sm:w-48 h-48 sm:h-auto flex-shrink-0 relative">
            <img 
              src={primaryImage} 
              alt={pharmacy.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/api/placeholder/400/250';
              }}
            />
            {pharmacy.isVerified && (
              <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                <CheckCircle size={12} />
                Verified
              </div>
            )}
            {openStatus !== null && (
              <div className={`absolute bottom-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${
                openStatus ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
              }`}>
                {openStatus ? '● Open' : '● Closed'}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 hover:text-green-600 cursor-pointer"
                    onClick={() => onPharmacyClick(pharmacy)}>
                  {pharmacy.name}
                </h3>
                {pharmacy.rating?.average > 0 && (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-1">
                      <Star size={16} className="fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold">{pharmacy.rating.average.toFixed(1)}</span>
                    </div>
                    <span className="text-sm text-gray-500">
                      ({pharmacy.rating.count} reviews)
                    </span>
                  </div>
                )}
              </div>
            </div>

            {pharmacy.description && (
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {pharmacy.description}
              </p>
            )}

            <div className="space-y-2 mb-4">
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <MapPin size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                <span>{pharmacy.address}</span>
              </div>
              
              {showDistance && pharmacy.distance && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Navigation size={16} className="text-green-600" />
                  <span>{pharmacy.distance.toFixed(1)} km away</span>
                </div>
              )}

              {pharmacy.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone size={16} className="text-green-600" />
                  <span>{pharmacy.phone}</span>
                </div>
              )}
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-2 mb-4">
              {pharmacy.deliveryService && (
                <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                  <Truck size={12} />
                  Delivery Available
                </span>
              )}
              {pharmacy.isVerified && (
                <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                  <Shield size={12} />
                  Licensed
                </span>
              )}
              {pharmacy.deliveryFee !== undefined && pharmacy.deliveryFee === 0 && (
                <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
                  Free Delivery
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => onPharmacyClick(pharmacy)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                View Details
              </button>
              {pharmacy.phone && (
                <button
                  onClick={() => onCallPharmacy(pharmacy.phone)}
                  className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors text-sm font-medium"
                >
                  Call Now
                </button>
              )}
              {pharmacy.deliveryService && (
                <button
                  onClick={() => window.location.href = `/pharmacies/${pharmacy._id}/order`}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Order Medicine
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer"
         onClick={() => onPharmacyClick(pharmacy)}>
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={primaryImage} 
          alt={pharmacy.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            e.target.src = '/api/placeholder/400/250';
          }}
        />
        {pharmacy.isVerified && (
          <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
            <CheckCircle size={12} />
            Verified
          </div>
        )}
        {openStatus !== null && (
          <div className={`absolute bottom-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${
            openStatus ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {openStatus ? '● Open' : '● Closed'}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors line-clamp-1">
          {pharmacy.name}
        </h3>

        {pharmacy.rating?.average > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-1">
              <Star size={14} className="fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-semibold">{pharmacy.rating.average.toFixed(1)}</span>
            </div>
            <span className="text-xs text-gray-500">
              ({pharmacy.rating.count})
            </span>
          </div>
        )}

        <div className="space-y-2 mb-3">
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <MapPin size={14} className="text-green-600 flex-shrink-0 mt-0.5" />
            <span className="line-clamp-1">{pharmacy.address}</span>
          </div>
          
          {showDistance && pharmacy.distance && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Navigation size={14} className="text-green-600" />
              <span>{pharmacy.distance.toFixed(1)} km away</span>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="flex flex-wrap gap-2 mb-4">
          {pharmacy.deliveryService && (
            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs">
              <Truck size={10} />
              Delivery
            </span>
          )}
          {pharmacy.isVerified && (
            <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded-full text-xs">
              <Shield size={10} />
              Licensed
            </span>
          )}
          {pharmacy.deliveryFee === 0 && pharmacy.deliveryService && (
            <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-2 py-1 rounded-full text-xs">
              Free
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPharmacyClick(pharmacy);
            }}
            className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
          >
            View Details
          </button>
          {pharmacy.phone && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCallPharmacy(pharmacy.phone);
              }}
              className="px-3 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors"
              title="Call pharmacy"
            >
              <Phone size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PharmacyCard;